import { State } from "@/shared/state/visualization";

// Panel ID -> ephemeral state
export type EphemeralPanelStateMap = Map<string, Record<string, unknown>>;

/**
 * Enriches a visualization state by merging ephemeral panel state into each panel's initParams.
 * This is used when sharing workspaces or saving layouts to capture ephemeral UI state
 * (viewport positions, zoom states, search filters, etc.) that should be restored when
 * the workspace/layout is loaded.
 *
 * @param vizState - The visualization state to enrich
 * @param ephemeralPanelState - Map of panel IDs to their ephemeral state
 * @returns A new State object with ephemeral state merged into panel initParams
 */
export function enrichVizStateWithEphemeralState(
  vizState: State,
  ephemeralPanelState?: EphemeralPanelStateMap,
): State {
  // Clone vizState to avoid mutating the original
  const enrichedVizState = structuredClone(vizState);

  // Merge ephemeral state into each panel's initParams
  if (ephemeralPanelState && enrichedVizState.panels) {
    for (const [panelId, ephemeralState] of ephemeralPanelState) {
      const panel = enrichedVizState.panels[panelId];
      if (panel) {
        panel.initParams = {
          ...(typeof panel.initParams === "object" && panel.initParams !== null
            ? panel.initParams
            : {}),
          ...ephemeralState,
        };
      }
    }
  }

  return enrichedVizState;
}
